<template>
  <div
    :id="`${inputName}-container`"
    class="hm-file-selector"
    :class="{ 'hm-file-selector--dragging': isDragging }"
  >
    <input
      @change="onChange"
      :name="inputName"
      type="file"
      :accept="accept"
      :id="inputName"
      class="d-none"
      ref="inputFile"
    />
    <div
      class="hm-file-selector-content-wrapper"
      :class="`hm-file-selector-content-wrapper--${status}`"
      @drop="drop"
      @dragend="draggingStop"
      @dragleave="draggingStop"
      @dragenter="dragging"
      @dragover.prevent="dragging"
    >
      <slot :data="{ inputName, error }" v-if="status === 'add'">
        <div class="hm-file-selector-content p-2">
          <div class="add-file">
            <h4>{{ title }}</h4>
            <p class="mt-3 mx-2 text-left" v-html="description"></p>
            <label class="btn btn-outline-gray upload-button" :for="inputName">
              <font-awesome-icon
                class="c-coral icon-image"
                :icon="['far', 'cloud-upload']"
              />
              {{ $t('GENERAL.BUTTON.UPLOAD') }}
            </label>

            <small class="drag-and-drop">
              {{ $t('GENERAL.TEXT.OR_DRAG_AND_DROP_YOUR_FILE_HERE') }}
            </small>
          </div>
        </div>
      </slot>
      <slot name="dragging" v-if="status === 'dragging'">
        <HMFixedAspectRatioContainer>
          <div class="hm-file-selector-content dragging-content">
            <font-awesome-icon
              class="dragging-icon"
              :icon="['far', 'file-upload']"
            />
            <p class="mt-3">
              {{ $t('GENERAL.TEXT.DROP_YOUR_FILE_HERE') }}
            </p>
          </div>
        </HMFixedAspectRatioContainer>
      </slot>
      <slot name="uploading" v-if="status === 'uploading'">
        <HMFixedAspectRatioContainer>
          <div class="hm-file-selector-content">
            <HMSpinner />
          </div>
        </HMFixedAspectRatioContainer>
      </slot>
      <slot name="uploaded" v-if="status === 'uploaded'" :data="{ inputName }">
        <HMFixedAspectRatioContainer>
          <div class="hm-file-selector-content">
            <div class="uploaded-options">
              <HMButton color="outline-gray" @click="remove">
                <font-awesome-icon
                  class="c-coral mr-2"
                  :icon="['far', 'trash']"
                />
                {{ $t('GENERAL.BUTTON.REMOVE') }}
              </HMButton>
            </div>
            <div class="uploaded-footer-wrapper">
              <div class="uploaded-footer">
                {{ `${fileDescription}` }}
              </div>
            </div>
          </div>
        </HMFixedAspectRatioContainer>
      </slot>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

export default Vue.extend({
  name: 'HMFileSelector',
  props: {
    value: {
      type: Object,
      default: null,
    },
    inputName: {
      type: String,
      default: 'upload-file',
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    fileDescription: {
      type: String,
      default: '',
    },
    accept: {
      type: String,
      default:
        'image/*,audio/*,video/*,application/pdf,application/msword,application/vnd.ms-excel,application/vnd.ms-powerpoint,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    },
  },
  data() {
    return {
      inputValue: null,
      error: null,
      isDragging: false,
    };
  },
  computed: {
    status() {
      if (this.isDragging) {
        return 'dragging';
      } else if (this.value) {
        return 'uploaded';
      } else {
        return 'add';
      }
    },
    acceptedFiles() {
      // if we want to remove the video upload option we should use this instead of the accept in <input> above
      if (!this.videoUploadAllowed) {
        return this.accept
          .split(',')
          .filter(e => e !== 'video/*')
          .join(',');
      }
      return this.accept;
    },
    mediaType() {
      return this.value?.file_content;
    },
  },
  methods: {
    isValidFile(inputFile) {
      if (this.accept === '*') return true;
      if (this.accept.includes(`${inputFile.type.split('/')[0]}/*`))
        return true;
      if (this.accept.includes(`${inputFile.type}`)) return true;

      return false;
    },
    async upload(inputFile) {
      if (!inputFile) return;
      if (!this.isValidFile(inputFile)) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'File type not supported',
        });
        return;
      }
      this.$emit('input', inputFile);
    },
    onChange(e) {
      const file = e.target.files[0];
      this.$refs.inputFile.value = null;
      this.upload(file);
    },
    drop: function (event) {
      event.stopPropagation();
      event.preventDefault();
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      this.upload(file);
    },
    updateFile(file) {
      this.$emit('input', file);
    },
    dragging() {
      this.isDragging = true;
    },
    draggingStop() {
      this.isDragging = false;
    },
    async remove() {
      const file = null;
      this.$emit('input', file);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.soft-16-9 {
  padding-top: 56.25%;
}

.hm-file-selector {
  border-radius: $border-radius;
  border-style: dashed;
  border-width: 1px;
  background-color: $lightest-gray;
  border-color: $medium-gray;
  padding: 0.5rem;
  position: relative;
  &--dragging {
    background-color: $light-gray;
  }
}

.hm-file-selector-content-wrapper {
  display: flex;
  border-radius: $border-radius;
  &--uploaded {
    background-color: $light-gray;
  }
}

.hm-file-selector-content {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
}

.dragging-icon {
  font-size: 1.5rem;
}

.add-file {
  text-align: center;
}

.upload-button {
  margin: 1.5rem 0 0;
}

.drag-and-drop {
  display: block;
  margin-top: 1rem;
  color: $medium-gray;
}

.icon-image {
  width: 18px;
  height: 18px;
}

.uploaded-options {
  display: none;
  position: absolute;
  right: 1rem;
  top: 1rem;
  z-index: 1;
}

.uploaded-footer-wrapper {
  position: absolute;
  overflow: hidden;
  bottom: 0;
  left: 0;
  right: 0;
}

.uploaded-footer {
  display: flex;
  align-items: center;
  min-height: 40px;
  height: auto !important;
  padding: 5px 1.5rem;
  background-color: rgba(black, 0.2);
  color: $white;
  border-bottom-left-radius: $border-radius;
  border-bottom-right-radius: $border-radius;
  backdrop-filter: blur(3px);
  transition: transform 0.2s;
  transform: translate(0, 100%);
}

.upload-label-button {
  padding: $popover-item-spacing-y $popover-item-spacing-x;
  margin: 0;
  display: flex;
  align-items: center;
}

.dragging-content {
  pointer-events: none;
  text-align: center;
}

.uploaded {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.hm-file-selector-content-wrapper:hover {
  .uploaded-options {
    display: flex;
  }
  .uploaded-footer {
    transition: transform 0.2s;
    transform: translate(0, 0);
  }
}
</style>
